import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Star } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { BUSINESS, HERO_HOME, IMG, HOME_GALLERY } from "../lib/constants";
import { SERVICES } from "../data/services";
import TrustStrip from "../components/TrustStrip";
import ServiceCard from "../components/ServiceCard";
import ReviewsSection from "../components/ReviewsSection";
import FaqSection from "../components/FaqSection";
import BookingCTA from "../components/BookingCTA";
import SEOHead from "../components/SEOHead";
import Lightbox from "../components/Lightbox";

const Home = () => {
    const { t } = useLanguage();
    const [lightboxIndex, setLightboxIndex] = useState(null);

    const openLightbox = (index) => setLightboxIndex(index);
    const closeLightbox = () => setLightboxIndex(null);
    const nextImage = () => setLightboxIndex((prev) => (prev + 1) % HOME_GALLERY.length);
    const prevImage = () => setLightboxIndex((prev) => (prev - 1 + HOME_GALLERY.length) % HOME_GALLERY.length);

    return (
        <>
            <SEOHead page="home" />
            {/* HERO */}
            <section
                data-testid="home-hero"
                className="relative min-h-[92vh] flex items-end overflow-hidden"
            >
                <div className="absolute inset-0">
                    <img
                        src={HERO_HOME}
                        alt="Beauty Ink LV permanent makeup studio Las Vegas - nano brows, lip blush, microblading"
                        className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#3D3D24] via-[#3D3D24]/55 to-[#3D3D24]/20" />
                </div>

                <div className="relative w-full max-w-[1400px] mx-auto px-6 pb-16 md:pb-24 z-10">
                    <div className="max-w-3xl bink-fade-up">
                        <div className="flex items-center gap-3 text-[#FFFFF3] mb-6 font-label text-[11px]">
                            <span className="flex items-center gap-1 drop-shadow-[0_0_8px_rgba(212,169,90,0.7)]">
                                {[...Array(5)].map((_, i) => (
                                    <Star key={i} size={14} fill="#F4C77A" stroke="#F4C77A" strokeWidth={2} />
                                ))}
                            </span>
                            <span>{t.home.overline}</span>
                        </div>
                        <h1
                            data-testid="hero-title"
                            className="font-serif text-[#FFFFF3] text-5xl md:text-7xl lg:text-8xl leading-[0.98] mb-7 max-w-[18ch]"
                        >
                            {t.home.heroTitle}
                        </h1>
                        <p className="text-[#FFFFF3]/90 text-base md:text-lg max-w-xl leading-relaxed mb-10">
                            {t.home.heroSub}
                        </p>
                        <div className="flex flex-col sm:flex-row gap-3 items-start">
                            <a
                                data-testid="hero-book-btn"
                                href="/contact"
                                className="bg-[#D4A95A] text-[#3D3D24] px-10 py-4 font-label text-xs hover:bg-[#FFFFF3] transition-colors inline-flex items-center gap-2"
                            >
                                {t.home.ctaPrimary}
                                <ArrowRight size={14} />
                            </a>
                            <Link
                                data-testid="hero-services-btn"
                                to="/nano-brows"
                                className="border border-[#FFFFF3]/40 text-[#FFFFF3] px-10 py-4 font-label text-xs hover:bg-[#FFFFF3] hover:text-[#3D3D24] transition-colors"
                            >
                                {t.home.ctaSecondary}
                            </Link>
                        </div>
                        <div className="mt-8 inline-flex items-center gap-3 bg-[#FFFFF3]/10 backdrop-blur-md border border-[#FFFFF3]/20 px-5 py-2.5">
                            <span className="w-1.5 h-1.5 bg-[#D4A95A] rounded-full animate-pulse" />
                            <span className="font-label text-[11px] text-[#FFFFF3]">
                                {t.home.seHabla}
                            </span>
                        </div>
                    </div>
                </div>
            </section>

            <TrustStrip variant="dark" />

            {/* SERVICES */}
            <section
                data-testid="home-services"
                className="bg-[#FFFFF3] py-24 md:py-32"
            >
                <div className="max-w-[1400px] mx-auto px-6">
                    <div className="grid lg:grid-cols-[1fr_1.5fr] gap-12 mb-16 items-end">
                        <div>
                            <div className="font-label text-[11px] text-[#D4A95A] mb-5">
                                {t.home.servicesOverline}
                            </div>
                            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#3D3D24] leading-[1.02]">
                                {t.home.servicesTitle}
                            </h2>
                        </div>
                        <p className="text-[#3D3D24]/80 text-base md:text-lg leading-relaxed lg:max-w-xl lg:justify-self-end">
                            {t.home.servicesSub}
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                        {SERVICES.map((s) => (
                            <ServiceCard
                                key={s.slug}
                                slug={s.slug}
                                serviceKey={s.key}
                                image={s.image}
                            />
                        ))}
                    </div>
                </div>
            </section>

            {/* ABOUT TEASER */}
            <section
                data-testid="home-about"
                className="bg-[#3D3D24] text-[#FFFFF3] py-24 md:py-32"
            >
                <div className="max-w-[1400px] mx-auto px-6 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
                    <div className="relative aspect-[4/5] overflow-hidden">
                        <img
                            src={IMG.wendyMentor}
                            alt="Wendy Chester - Permanent Makeup Artist Las Vegas PMUA Advanced Esthetician"
                            className="w-full h-full object-cover bink-img-hover"
                        />
                        <div className="absolute bottom-6 left-6 bg-[#D4A95A] text-[#3D3D24] px-5 py-3">
                            <div className="font-label text-[10px] tracking-[0.2em] opacity-80">Owner · Artist</div>
                            <div className="font-serif text-2xl">Wendy Chester</div>
                        </div>
                    </div>
                    <div>
                        <div className="font-label text-[11px] text-[#D4A95A] mb-5">
                            {t.home.aboutOverline}
                        </div>
                        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] mb-7">
                            {t.home.aboutTitle}
                        </h2>
                        <p className="text-base md:text-lg leading-relaxed opacity-85 mb-8 max-w-xl">
                            {t.home.aboutBody}
                        </p>
                        <Link
                            data-testid="home-about-link"
                            to="/about"
                            className="inline-flex items-center gap-2 border border-[#FFFFF3]/40 text-[#FFFFF3] px-8 py-3.5 font-label text-xs hover:bg-[#D4A95A] hover:text-[#3D3D24] hover:border-[#D4A95A] transition-colors"
                        >
                            {t.home.aboutCta}
                            <ArrowRight size={14} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* GALLERY — horizontal scroll */}
            <section
                data-testid="home-gallery"
                className="bg-[#FFFFF3] py-24 md:py-32"
            >
                <div className="max-w-[1400px] mx-auto px-6 mb-10">
                    <div className="text-left">
                        <div className="font-label text-[11px] text-[#D4A95A] mb-5">
                            {t.home.galleryOverline}
                        </div>
                        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#3D3D24] leading-[1.05] max-w-2xl">
                            {t.home.galleryTitle}
                        </h2>
                    </div>
                </div>
                <div
                    data-testid="home-gallery-scroll"
                    className="bink-hscroll flex gap-4 md:gap-6 overflow-x-auto pb-6 px-6 md:px-12 scroll-smooth"
                >
                    {HOME_GALLERY.map((src, i) => (
                        <div
                            key={i}
                            data-testid={`gallery-img-${i}`}
                            className="shrink-0 w-[260px] md:w-[340px] lg:w-[380px] aspect-[4/5] overflow-hidden bg-[#F5F2E4] cursor-pointer group"
                            onClick={() => openLightbox(i)}
                        >
                            <img
                                src={src}
                                alt={`Permanent makeup Las Vegas results - nano brows lip blush by Wendy Chester ${i + 1}`}
                                className="w-full h-full object-cover bink-crop-bottom group-hover:scale-105 transition-transform duration-300"
                            />
                        </div>
                    ))}
                </div>
                <div className="max-w-[1400px] mx-auto px-6 mt-8">
                    <div className="font-label text-[10px] tracking-[0.2em] text-[#3D3D24]/60">
                        ← Scroll for more →
                    </div>
                </div>
            </section>

            {/* Lightbox */}
            {lightboxIndex !== null && (
                <Lightbox
                    images={HOME_GALLERY}
                    currentIndex={lightboxIndex}
                    onClose={closeLightbox}
                    onNext={nextImage}
                    onPrev={prevImage}
                />
            )}

            <ReviewsSection />
            <FaqSection />
            <BookingCTA variant="dark" />
        </>
    );
};

export default Home;
